import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  FiBook, 
  FiUsers, 
  FiCalendar,  
  FiUser, 
  FiUserCheck, 
  FiAlertCircle, 
  FiRefreshCw,
  FiSettings,
  FiMapPin,
  FiInfo,
  FiGlobe,
  FiAward,
  FiLoader,
  FiLock,
  FiCpu,
  FiBarChart2,
  FiChevronRight,
  FiLayers,
  FiX
} from 'react-icons/fi';
import TenantAdminLayout from '../../Layout/TenantAdminLayout';
import api from '../../../api';
import FacultyAssignmentModal from '../modals/FacultyAssignmentModal';
import LabBatchManagementModal from '../modals/LabBatchManagementModal';
import ElectiveStudentsModal from '../modals/ElectiveStudentsModal';
import EditClassModal from '../modals/EditClassModal';

// ✅ Add skipLayout prop
const ClassDetailsPage = ({ skipLayout = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Get data from navigation state
  const navigationState = location.state || {};
  const {
    classId,
    classes = [],
    departments = [],
    subjects = [],
    allPrograms = [],
    allProgramTypes = [],
    academicYearStatus = 'active',
    isViewOnly = false,
    tenantId,
    userId,
    organizationId,
    semesters = [],
    terms = [],
  } = navigationState;

  // -------- STATE HOOKS (Preserved) --------
  const [classDetails, setClassDetails] = useState(null);
  const [facultyAssignments, setFacultyAssignments] = useState([]);
  const [students, setStudents] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [facultyLoading, setFacultyLoading] = useState(true);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeModal, setActiveModal] = useState(null);

  // -------- DERIVED STATE (Preserved) --------
  const isCompletedAcademicYear = academicYearStatus === 'completed' || isViewOnly;
  const selectedClass = classes.find((cls) => String(cls._id) === String(classId));

  // -------- API FUNCTIONS (Preserved) --------
  const fetchClassDetails = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/api/classManager/class-details/${classId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setClassDetails(response.data.classData);
      } else {
        if (selectedClass) {
          setClassDetails(selectedClass);
        } else {
          toast.error('Class not found');
        }
      }
    } catch (error) {
      console.error('Error fetching class details:', error);
      if (selectedClass) {
        setClassDetails(selectedClass);
      } else {
        toast.error('Failed to load class details');
      }
    } finally {
      setLoading(false);
    }
  }, [classId, selectedClass]);

  const fetchFacultyAssignments = useCallback(async () => {
    try {
      setFacultyLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get(
        `/api/classManager/faculty-assignments/${classId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFacultyAssignments(response.data.facultyAssignments || []);
    } catch (err) {
      console.error('Error fetching faculty assignments:', err);
      if (err.response?.status !== 404) {
        toast.error('Failed to load faculty assignments');
      }
      setFacultyAssignments([]);
    } finally {
      setFacultyLoading(false);
    }
  }, [classId]);

  const fetchStudents = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(
        `/api/classManager/students-in-class/${classId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudents(res.data.students || []);
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudents([]);
    }
  }, [classId]);

  const fetchClassSubjects = useCallback(async (effectiveClassId) => {
    try {
      setSubjectsLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get(
        `/api/classManager/subjects-for-class/${effectiveClassId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const subs = res.data.subjects || res.data || [];
      const fetchedSubjects = Array.isArray(subs) ? subs : [];
      
      setClassSubjects(fetchedSubjects);
      
      if (fetchedSubjects.length > 0) {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching subjects for class:', err);
      setClassSubjects([]);
      toast.error('Failed to load class subjects');
      
      if (classDetails) {
        setLoading(false);
      }
    } finally {
      setSubjectsLoading(false);
    }
  }, [classDetails]);

  // -------- EFFECTS (Preserved) --------
  useEffect(() => {
    if (classId) {
      fetchClassDetails();
      fetchFacultyAssignments();
      fetchStudents();
      fetchClassSubjects(classId);
    }
  }, [classId, fetchClassDetails, fetchFacultyAssignments, fetchStudents, fetchClassSubjects]);

  useEffect(() => {
    if (!classId) {
      toast.error('Class information not found.');
      navigate(-1);
    }
  }, [classId, navigate]);

  // -------- HELPER FUNCTIONS (Preserved) --------
  const getAssignedFacultyForSubject = (subjectId) => {
    const assignment = facultyAssignments.find((assignment) =>
      assignment.subjects?.some(
        (subj) => String(subj.subjectId) === String(subjectId)
      )
    );

    if (assignment) {
      const subjectAssignment = assignment.subjects.find(
        (subj) => String(subj.subjectId) === String(subjectId)
      );
      return {
        facultyId: assignment.facultyId,
        facultyName: assignment.facultyName,
        facultyEmail: assignment.facultyEmail,
        role: subjectAssignment?.role || 'Primary Teacher',
        assignedAt: assignment.assignedAt
      };
    }
    return null;
  };

  const getAllAssignedFaculty = () => {
    const uniqueFaculty = [];
    const seenFacultyIds = new Set();

    facultyAssignments.forEach((assignment) => {
      if (!seenFacultyIds.has(assignment.facultyId)) {
        seenFacultyIds.add(assignment.facultyId);
        uniqueFaculty.push({
          facultyId: assignment.facultyId,
          facultyName: assignment.facultyName,
          facultyEmail: assignment.facultyEmail,
          assignedAt: assignment.assignedAt,
          subjectCount: assignment.subjects?.length || 0,
          subjects: assignment.subjects || []
        });
      }
    });

    return uniqueFaculty;
  };

  const getDepartmentDetails = () => {
    if (!classDetails) return [];
    
    if (classDetails.departmentDetails && Array.isArray(classDetails.departmentDetails)) {
      return classDetails.departmentDetails.map(dept => ({
        id: dept.departmentId || dept.id,
        name: dept.departmentName || dept.name,
        isPrimary: dept.isPrimary || false,
        totalSubjects: dept.totalSubjects || 0,
        subjectIds: dept.subjectIds || []
      }));
    }
    
    if (classDetails.departmentSubjects && Array.isArray(classDetails.departmentSubjects)) {
      return classDetails.departmentSubjects.map((dept, index) => {
        const isPrimary = dept.departmentId === classDetails.departmentId || 
                         (Array.isArray(classDetails.departmentIds) && 
                          classDetails.departmentIds[0] === dept.departmentId);
        
        return {
          id: dept.departmentId,
          name: dept.departmentName,
          isPrimary,
          totalSubjects: dept.totalSubjects || 0,
          subjectIds: dept.subjectIds || []
        };
      });
    }
    
    const primaryDepartmentId = classDetails.departmentId ||
      (Array.isArray(classDetails.departmentIds) && classDetails.departmentIds[0]);
    
    const dept = departments.find(
      d => String(d._id) === String(primaryDepartmentId) || String(d.id) === String(primaryDepartmentId)
    );
    
    return dept ? [{
      id: primaryDepartmentId,
      name: dept.name || dept.departmentName,
      isPrimary: true,
      totalSubjects: 0,
      subjectIds: []
    }] : [];
  };



  const getSubjectsByDepartment = () => {
    if (!classDetails || !Array.isArray(classDetails.departmentSubjects)) return {};

    const subjectMap = {};
    effectiveSubjects.forEach(subj => {
      const id = subj._id ? String(subj._id) : (subj.subjectId ? String(subj.subjectId) : null);
      if (id) subjectMap[id] = subj;
    });

    const subjectsByDept = {};

    classDetails.departmentSubjects.forEach(dept => {
      const deptId = String(dept.departmentId);
      subjectsByDept[deptId] = {
        department: {
          id: deptId,
          name: dept.departmentName,
          isPrimary: classDetails.departmentId === deptId,
          totalSubjects: dept.totalSubjects,
          subjectIds: dept.subjectIds
        },
        subjects: (dept.subjectIds || [])
          .map(sid => subjectMap[String(sid)])
          .filter(Boolean)
      };
    });

    return subjectsByDept;
  };

  const getProgramInfo = () => {
    if (!classDetails) return { typeCode: 'N/A', programName: 'N/A' };
    
    let typeCode = classDetails.programTypeCode || 
                   (classDetails.programTypeId && typeof classDetails.programTypeId === 'object' ? classDetails.programTypeId.code : '') || 
                   '';
    
    let programName = classDetails.programName || 
                      (classDetails.programId && typeof classDetails.programId === 'object' ? classDetails.programId.name : '') || 
                      '';

    if ((!typeCode || !programName) && Array.isArray(allPrograms) && allPrograms.length > 0 && classDetails.programId) {
      const programObj = allPrograms.find(
        p => String(p._id) === String(classDetails.programId)
      );
      if (programObj) {
        programName = programName || programObj.name || '';
        
        if (programObj.programTypeId) {
          const programType = typeof programObj.programTypeId === 'object' 
            ? programObj.programTypeId 
            : allPrograms.find(p => p._id === programObj.programTypeId);
          
          if (programType) {
            typeCode = typeCode || programType.code || programType.name || '';
          }
        }
      }
    }

    return { 
      typeCode: typeCode || 'N/A', 
      programName: programName || 'N/A' 
    };
  };

  const getSubjectTypeInfo = (subject) => {
    const isLanguage = !!subject.isLanguage || !!subject.isLanguageElective;
    const isElective = !!subject.isElective;
    const isLab = subject.isLab === true || subject.type === 'lab' || subject.subjectType === 'lab';
    const isGroupManaged = subject.requiresGroupManagement === true;
    const subjectCategory = (subject.subjectCategory || 'academic').toLowerCase();
    const languageType = (subject.languageType || '').toLowerCase();

    if (isLanguage && isGroupManaged) {
      return {
        type: 'language_group',
        label: 'Language Choice',
        color: 'blue',
        icon: FiGlobe,
        badgeClass: 'bg-blue-50 text-blue-700 border border-blue-200',
        iconBgClass: 'bg-blue-50 text-blue-600',
        description: 'Choice-based language'
      };
    }

    if (isLanguage) {
      return {
        type: 'language_common',
        label: languageType === 'choice-based' ? 'Language Choice' : 'Main Language',
        color: 'blue',
        icon: FiGlobe,
        badgeClass: 'bg-blue-50 text-blue-700 border border-blue-100',
        iconBgClass: 'bg-blue-50 text-blue-600',
        description: 'Language Subject'
      };
    }

    if (isElective && isGroupManaged) {
      return {
        type: 'elective_group',
        label: 'Open Choice Subject',
        color: 'purple',
        icon: FiAward,
        badgeClass: 'bg-purple-50 text-purple-700 border border-purple-200',
        iconBgClass: 'bg-purple-50 text-purple-600',
        description: 'Optional subject'
      };
    }

    if (isElective) {
      return {
        type: 'elective_common',
        label: 'Common Optional',
        color: 'purple',
        icon: FiAward,
        badgeClass: 'bg-purple-50 text-purple-700 border border-purple-100',
        iconBgClass: 'bg-purple-50 text-purple-600',
        description: 'Standard optional subject'
      };
    }

    if (isLab) {
      return {
        type: 'lab',
        label: 'Practical / Lab',
        color: 'orange',
        icon: FiSettings,
        badgeClass: 'bg-orange-50 text-orange-700 border border-orange-200',
        iconBgClass: 'bg-orange-50 text-orange-600',
        description: 'Lab Work'
      };
    }

    const isAcademic = subjectCategory === 'academic';

    return {
      type: isAcademic ? 'regular_academic' : 'regular_non_academic',
      label: isAcademic ? 'Main Subject' : 'Extra Activity',
      color: 'slate',
      icon: FiBook,
      badgeClass: isAcademic
        ? 'bg-slate-100 text-slate-700 border border-slate-200'
        : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      iconBgClass: isAcademic
        ? 'bg-slate-100 text-slate-600'
        : 'bg-emerald-50 text-emerald-600',
      description: isAcademic ? 'Regular subject' : 'Extra activity'
    };
  };

  // -------- SUBJECT RESOLUTION (Preserved) --------
  const effectiveSubjects = classSubjects.length > 0 ? classSubjects : (Array.isArray(subjects) ? subjects : []);
  
  const getSubjectIdString = (subj) => {
    if (!subj) return null;
    if (subj._id && typeof subj._id === 'object' && subj._id.$oid) {
      return String(subj._id.$oid);
    }
    if (subj._id) return String(subj._id);
    if (subj.id) return String(subj.id);
    return null;
  };

  const resolvedClassSubjects = (classDetails?.subjects || [])
    .map((subjectId) => {
      const sid = String(subjectId);
      return effectiveSubjects.find((subj) => getSubjectIdString(subj) === sid);
    })
    .filter(Boolean) || [];

  const isLanguageSubject = (s) => !!s.isLanguage || !!s.isLanguageElective;
  const isElectiveSubject = (s) => !!s.isElective;
  const isLabSubject = (s) => s.isLab === true || s.type === 'lab' || s.subjectType === 'lab';
  const isGroupManagedSubject = (s) => s.requiresGroupManagement === true;

  const languageElectiveSubjects = resolvedClassSubjects.filter(
    (subject) => isLanguageSubject(subject) && isGroupManagedSubject(subject)
  );

  const regularElectiveSubjects = resolvedClassSubjects.filter(
    (subject) => isElectiveSubject(subject) && !isLanguageSubject(subject) && isGroupManagedSubject(subject)
  );

  const labSubjects = resolvedClassSubjects.filter((subject) => isLabSubject(subject));

  const regularSubjects = resolvedClassSubjects.filter((subject) => {
    if (isLabSubject(subject)) return false;
    if (isGroupManagedSubject(subject) && (isLanguageSubject(subject) || isElectiveSubject(subject))) {
      return false;
    }
    return true;
  });

  // Calculations
  const allSubjectsMap = {};
  [...resolvedClassSubjects, ...labSubjects].forEach((subject) => {
    if (subject && subject._id) {
      allSubjectsMap[subject._id] = subject;
    }
  });
  const uniqueSubjects = Object.values(allSubjectsMap);
  const totalSubjects = uniqueSubjects.length;
  const assignedSubjects = [...regularSubjects, ...labSubjects].filter((subject) =>
    getAssignedFacultyForSubject(subject._id)
  ).length;

  const assignmentBaseCount = regularSubjects.length + labSubjects.length;
  const assignmentPercentage = assignmentBaseCount > 0
    ? Math.round((assignedSubjects / assignmentBaseCount) * 100)
    : 0;

  const allAssignedFaculty = getAllAssignedFaculty();
  const deptLevelSubjects = [...languageElectiveSubjects, ...regularElectiveSubjects];
  const classLevelSubjects = [...regularSubjects, ...labSubjects];
  const classLevelAssigned = classLevelSubjects.filter(subject =>
    getAssignedFacultyForSubject(subject._id)
  ).length;

  const departmentDetails = getDepartmentDetails();
  const totalDepartments = departmentDetails.length;
  const isMultiDepartment = totalDepartments > 1;
  const subjectsByDepartment = getSubjectsByDepartment();

  // -------- ACTION HANDLERS (Preserved) --------
  const handleEditClass = () => {
    if (isCompletedAcademicYear) {
      toast.warning('Editing is disabled for closed years.');
      return;
    }
    setActiveModal({
      type: 'editClass',
      props: { 
        classId: classDetails._id || classId,
        className: classDetails.className,
        classObj: classDetails,
        isViewOnly: isCompletedAcademicYear,
        isSemesterBased: classDetails.isSemesterBased,
        semesterId: classDetails.semesterId,
        termId: classDetails.termId
      }
    });
  };

  const handleFacultyAssignment = () => {
    if (isCompletedAcademicYear) {
      toast.warning('Teacher lists cannot be changed for closed years.');
      return;
    }
    setActiveModal({
      type: 'facultyAssignment',
      props: { 
        classId: classDetails._id || classId,
        className: classDetails.className,
        subjects: [...(regularSubjects || []), ...(labSubjects || [])],
        isViewOnly: isCompletedAcademicYear
      }
    });
  };

  const handleManageElectiveStudents = (electiveSubjects, groupType) => {
    if (isCompletedAcademicYear) {
      toast.warning('Student choices cannot be changed for closed years.');
      return;
    }
    setActiveModal({
      type: 'manageElectiveStudents',
      props: { 
        classId,
        classObj: classDetails,
        className: classDetails.className,
        electiveSubjects,
        groupType,
        departmentId: null,
        isViewOnly: isCompletedAcademicYear
      }
    });
  };

  const handleLabBatchManagement = () => {
    if (isCompletedAcademicYear) {
      toast.warning('Lab settings cannot be changed for closed years.');
      return;
    }
    setActiveModal({
      type: 'labBatchManagement',
      props: {
        classId: classDetails._id || classId,
        className: classDetails.className,
        labSubjects: labSubjects || [],
        isViewOnly: isCompletedAcademicYear,
        isSemesterBased: classDetails.isSemesterBased,
        semesterId: classDetails.semesterId,
        termId: classDetails.termId,
        semesterNumber: classDetails.semesterNumber,
        termNumber: classDetails.termNumber,
        tenantId: tenantId,
        userId: userId,
        organizationId: organizationId
      }
    });
  };

  const closeModal = async () => {
    if (
      activeModal?.type === 'facultyAssignment' ||
      activeModal?.type === 'labBatchManagement' ||
      activeModal?.type === 'manageElectiveStudents' ||
      activeModal?.type === 'editClass'
    ) {
      await fetchClassDetails();
      await fetchFacultyAssignments();
      await fetchStudents();
      await fetchClassSubjects(classId);
    }
    setActiveModal(null);
  };

  // -------- DESIGNS & SUB-COMPONENTS (Simplified Text) --------
  const renderDepartmentOverview = () => (
    <div className="space-y-4">
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <FiLayers className="text-slate-500" />
        <h3 className="font-semibold text-slate-800 text-xs uppercase tracking-wider">Connected Departments</h3>
      </div>

      {isMultiDepartment && (
        <div className="p-3 bg-amber-50 text-amber-950 border border-amber-200 rounded-xl text-xs flex gap-2.5 items-start shadow-sm">
          <FiInfo className="mt-0.5 flex-shrink-0 text-amber-700" size={15} />
          <div>
            <span className="font-bold block text-amber-900">Shared Class Warning</span>
            This class is shared across {totalDepartments} different departments.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {departmentDetails.map((dept) => (
          <div 
            key={dept.id} 
            className={`p-4 rounded-xl border transition-all duration-200 ${
              dept.isPrimary 
                ? 'bg-slate-50 border-indigo-200 shadow-sm ring-1 ring-indigo-50/50' 
                : 'bg-slate-50/70 border-slate-200 shadow-xs'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <h4 className="font-bold text-slate-900 text-sm tracking-tight">{dept.name}</h4>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {dept.isPrimary && (
                    <span className="bg-indigo-600 text-white text-[9px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-md">
                      Main Dept
                    </span>
                  )}
                  <span className="bg-slate-200 text-slate-700 text-[10px] font-semibold px-2 py-0.5 rounded-md border border-slate-300/40">
                    {subjectsByDepartment[dept.id]?.subjects.length || 0} Subjects
                  </span>
                </div>
              </div>
            </div>
            
            {subjectsByDepartment[dept.id]?.subjects.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600 space-y-1.5">
                {subjectsByDepartment[dept.id].subjects.slice(0, 2).map(subject => (
                  <div key={subject._id || subject.subjectId} className="truncate font-medium text-slate-700 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 block flex-shrink-0"></span>
                    <span className="truncate">{subject.name || subject.subjectName}</span>
                  </div>
                ))}
                {subjectsByDepartment[dept.id].subjects.length > 2 && (
                  <div className="text-indigo-600 font-bold text-[11px] pl-3 pt-0.5">
                    + {subjectsByDepartment[dept.id].subjects.length - 2} More Subjects
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Quick Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl shadow-xs">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Subjects</span>
            <FiBook className="text-slate-500" size={16} />
          </div>
          <p className="text-2xl font-black text-slate-900 mt-2">{totalSubjects}</p>
          <div className="flex gap-2 text-[10px] text-slate-600 mt-2 font-bold">
            <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700">{classLevelSubjects.length} Core</span>
            <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-100">{deptLevelSubjects.length} Shared</span>
          </div>
        </div>

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl shadow-xs">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Assigned Teachers</span>
            <FiUserCheck className="text-emerald-600" size={16} />
          </div>
          <p className="text-2xl font-black text-slate-900 mt-2">
            {classLevelAssigned}
            <span className="text-xs text-slate-400 font-normal"> / {classLevelSubjects.length}</span>
          </p>
          <div className="mt-3 w-full bg-slate-200 rounded-full h-2 overflow-hidden border border-slate-300/30">
            <div 
              className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${assignmentPercentage}%` }}
            ></div>
          </div>
        </div>

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl shadow-xs">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Active Teachers</span>
            <FiUsers className="text-indigo-600" size={16} />
          </div>
          <p className="text-2xl font-black text-slate-900 mt-2">{allAssignedFaculty.length}</p>
          <p className="text-[10px] text-slate-500 mt-2 font-semibold bg-slate-200/60 inline-block px-1.5 py-0.5 rounded truncate max-w-full">
            {allAssignedFaculty.length > 0 ? 'Teachers Linked' : 'No Teachers Linked'}
          </p>
        </div>

        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl shadow-xs">
          <div className="flex justify-between items-start">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Departments</span>
            <FiLayers className="text-amber-600" size={16} />
          </div>
          <p className="text-2xl font-black text-slate-900 mt-2">{totalDepartments}</p>
          <p className="text-[10px] text-slate-500 mt-2 font-semibold bg-slate-200/60 inline-block px-1.5 py-0.5 rounded truncate max-w-full">
            {isMultiDepartment ? 'Multiple Depts' : 'Single Dept'}
          </p>
        </div>
      </div>

      {/* Dynamic Department Segment */}
      {renderDepartmentOverview()}

      {/* Grid Splitter for Academic Info and Controls */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            <FiCpu className="text-slate-600" />
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Class Info</h3>
          </div>
          <div className="divide-y divide-slate-200 text-sm">
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600 font-semibold">Course Code</span>
              <span className="font-bold text-slate-900 bg-slate-200 px-2 py-0.5 rounded">{programInfo.typeCode}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600 font-semibold">Course Name</span>
              <span className="font-bold text-slate-800 text-right max-w-[280px] truncate">{programInfo.programName}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600 font-semibold">Type</span>
              <span className="font-bold text-slate-800 bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded border border-indigo-100">
                {classDetails.isSemesterBased ? `Semester ${classDetails.semesterNumber}` : 'Yearly Class'}
              </span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600 font-semibold">Max Students Allowed</span>
              <span className="font-bold text-slate-900">{classDetails.maxStudents ?? 'No Limit'}</span>
            </div>
            <div className="flex justify-between items-center py-3">
              <span className="text-slate-600 font-semibold">Created On</span>
              <span className="text-slate-700 flex items-center gap-1.5 font-semibold">
                <FiCalendar className="text-slate-500" size={13} />
                {new Date(classDetails.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 bg-slate-50 border border-slate-200 rounded-xl p-5 shadow-xs space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            <FiSettings className="text-slate-600" />
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Actions</h3>
          </div>
          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleFacultyAssignment}
              disabled={isCompletedAcademicYear}
              className={`flex items-center justify-between p-3.5 rounded-xl border text-left transition-all ${
                isCompletedAcademicYear
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                  : 'bg-white text-slate-800 font-semibold border-slate-200 hover:border-indigo-600 hover:bg-slate-50/50 shadow-xs'
              }`}
            >
              <span className="text-xs tracking-tight">Assign/Manage Teachers</span>
              <FiChevronRight className="text-slate-400" size={16} />
            </button>

            {labSubjects.length > 0 && (
              <button
                onClick={handleLabBatchManagement}
                disabled={isCompletedAcademicYear}
                className={`flex items-center justify-between p-3.5 rounded-xl border text-left transition-all ${
                  isCompletedAcademicYear
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                    : 'bg-white text-slate-800 font-semibold border-slate-200 hover:border-amber-600 hover:bg-slate-50/50 shadow-xs'
                }`}
              >
                <span className="text-xs tracking-tight">
                  {classDetails.hasLabBatches ? 'Manage Lab Batches' : 'Setup Lab Groups'}
                </span>
                <FiChevronRight className="text-slate-400" size={16} />
              </button>
            )}

            {regularElectiveSubjects.length > 0 && (
              <button
                onClick={() => handleManageElectiveStudents(regularElectiveSubjects, 'elective')}
                disabled={isCompletedAcademicYear}
                className={`flex items-center justify-between p-3.5 rounded-xl border text-left transition-all ${
                  isCompletedAcademicYear
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                    : 'bg-white text-slate-800 font-semibold border-slate-200 hover:border-indigo-600 hover:bg-slate-50/50 shadow-xs'
                }`}
              >
                <span className="text-xs tracking-tight">Manage Optional Subjects</span>
                <FiChevronRight className="text-slate-400" size={16} />
              </button>
            )}

            {languageElectiveSubjects.length > 0 && (
              <button
                onClick={() => handleManageElectiveStudents(languageElectiveSubjects, 'language')}
                disabled={isCompletedAcademicYear}
                className={`flex items-center justify-between p-3.5 rounded-xl border text-left transition-all ${
                  isCompletedAcademicYear
                    ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                    : 'bg-white text-slate-800 font-semibold border-slate-200 hover:border-blue-600 hover:bg-slate-50/50 shadow-xs'
                }`}
              >
                <span className="text-xs tracking-tight">Manage Language Subjects</span>
                <FiChevronRight className="text-slate-400" size={16} />
              </button>
            )}

            <button
              onClick={handleEditClass}
              disabled={isCompletedAcademicYear}
              className={`flex items-center justify-between p-3.5 rounded-xl border text-left transition-all ${
                isCompletedAcademicYear
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                  : 'bg-white text-slate-800 font-semibold border-slate-200 hover:border-emerald-600 hover:bg-slate-50/50 shadow-xs'
              }`}
            >
              <span className="text-xs tracking-tight">Edit Class Details</span>
              <FiChevronRight className="text-slate-400" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderFacultyTab = () => (
    <div className="space-y-6">
      {/* Allocation Metrics Banner */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xs">
        <div>
          <h4 className="font-bold text-slate-900 text-sm">Teacher Assignment Progress</h4>
          <p className="text-slate-600 text-xs mt-0.5">
            {classLevelAssigned} out of {classLevelSubjects.length} main subjects have teachers assigned.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xl font-black text-slate-900">{assignmentPercentage}%</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Done</div>
          </div>
          <div className="w-20 bg-slate-200 rounded-full h-2.5 overflow-hidden border border-slate-300/20">
            <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${assignmentPercentage}%` }}></div>
          </div>
        </div>
      </div>

      {/* Staff Rollout */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Class Teachers</h3>
          <span className="bg-slate-200 border border-slate-300/50 text-slate-800 text-xs font-bold px-2.5 py-0.5 rounded-md">
            {allAssignedFaculty.length} Active
          </span>
        </div>

        {facultyLoading ? (
          <div className="flex items-center justify-center py-12 bg-slate-50 rounded-xl border border-slate-200">
            <FiLoader className="animate-spin text-indigo-600" size={24} />
          </div>
        ) : allAssignedFaculty.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {allAssignedFaculty.map((faculty) => (
              <div key={faculty.facultyId} className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3.5 shadow-xs hover:border-slate-300 transition-all">
                <div className="w-10 h-10 bg-indigo-50 border border-indigo-100 rounded-xl flex items-center justify-center text-indigo-600 flex-shrink-0">
                  <FiUserCheck size={18} />
                </div>
                <div className="space-y-1 min-w-0 flex-1">
                  <h4 className="font-bold text-slate-900 text-sm truncate">{faculty.facultyName}</h4>
                  <p className="text-xs text-slate-600 truncate font-medium">{faculty.facultyEmail}</p>
                  <div className="pt-1.5">
                    <span className="inline-block bg-emerald-50 text-emerald-800 font-bold text-[9px] uppercase tracking-wider px-2 py-0.5 border border-emerald-200 rounded-md">
                      Teaches {faculty.subjectCount} Subjects
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-slate-50 rounded-xl border border-dashed border-slate-300">
            <FiUser className="mx-auto text-slate-400 mb-2" size={28} />
            <h4 className="font-bold text-slate-700 text-sm">No Teachers Added Yet</h4>
            <p className="text-slate-500 text-xs mt-1 max-w-xs mx-auto">
              {isCompletedAcademicYear ? 'This year is closed and locked.' : 'Start by adding teachers to the subjects.'}
            </p>
          </div>
        )}
      </div>

      {/* Department Managed Context */}
      {deptLevelSubjects.length > 0 && (
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
            <FiLayers className="text-slate-500" size={14} />
            <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">Shared Subjects Information</h3>
          </div>
          <div className="p-3.5 bg-indigo-50/60 text-indigo-950 border border-indigo-100 rounded-xl text-xs flex gap-2.5 shadow-xs">
            <FiInfo className="mt-0.5 flex-shrink-0 text-indigo-600" size={15} />
            <p className="text-slate-700 font-medium">
              Note: Optional subjects and extra languages are managed directly by the main department office, not inside this specific class screen.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {deptLevelSubjects.map((subject) => {
              return (
                <div key={subject._id} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs shadow-xs">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="p-2 bg-white border border-slate-200 rounded-lg text-slate-500 flex-shrink-0">
                      <FiBook size={14} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-900 truncate">{subject.name}</p>
                      <p className="text-slate-500 font-mono text-[10px] mt-0.5 truncate">{subject.subjectCode}</p>
                    </div>
                  </div>
                  <span className="bg-slate-200 border border-slate-300 text-slate-700 font-bold px-2 py-0.5 rounded text-[9px] uppercase tracking-wider flex-shrink-0">
                    Dept Main
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const renderSubjectsTab = () => (
    <div className="space-y-6">
      {subjectsLoading && (
        <div className="p-3 bg-indigo-50 text-indigo-900 border border-indigo-200 rounded-xl text-xs flex gap-2 items-center font-medium shadow-xs">
          <FiLoader className="animate-spin text-indigo-600" size={15} />
          Loading the list of subjects...
        </div>
      )}

      {/* Core Local Catalog */}
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-slate-200 pb-2">
          <h3 className="font-bold text-slate-800 text-xs uppercase tracking-wider">List of Subjects</h3>
          <span className="bg-slate-200 border border-slate-300/60 text-slate-800 text-xs font-bold px-2.5 py-0.5 rounded-md">
            {classLevelSubjects.length} Added
          </span>
        </div>

        {regularSubjects.length > 0 && (
          <div className="space-y-3">
            <span className="text-[10px] font-extrabold tracking-widest text-slate-500 uppercase block pl-1">Theory Subjects</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {regularSubjects.map((subject) => {
                const assignedFaculty = getAssignedFacultyForSubject(subject._id);
                const typeInfo = getSubjectTypeInfo(subject);
                return (
                  <div key={subject._id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-start shadow-xs hover:border-slate-300 transition-colors">
                    <div className="space-y-1.5 min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-slate-900 text-sm truncate">{subject.name}</h4>
                        <span className={`${typeInfo.badgeClass} text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded`}>
                          {typeInfo.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono font-medium">{subject.subjectCode}</p>
                      {subject.isCreditBased && (
                        <span className="inline-block bg-slate-200/80 text-slate-700 border border-slate-300/40 font-bold text-[10px] px-2 py-0.5 rounded">
                          {subject.credits} Credits
                        </span>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      {assignedFaculty ? (
                        <div className="space-y-1">
                          <p className="text-xs font-bold text-indigo-600 truncate max-w-[140px]">{assignedFaculty.facultyName}</p>
                          <p className="text-[9px] text-slate-500 bg-slate-200 font-bold uppercase tracking-wider px-1.5 py-0.5 rounded inline-block">{assignedFaculty.role}</p>
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded border border-amber-200">No Teacher</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {labSubjects.length > 0 && (
          <div className="space-y-3 pt-3">
            <span className="text-[10px] font-extrabold tracking-widest text-slate-500 uppercase block pl-1">Practical & Lab Work</span>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {labSubjects.map((subject) => {
                const assignedFaculty = getAssignedFacultyForSubject(subject._id);
                return (
                  <div key={subject._id} className="p-4 bg-slate-50 border border-amber-200 rounded-xl flex justify-between items-start shadow-xs">
                    <div className="space-y-1.5 min-w-0 flex-1 pr-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-bold text-slate-900 text-sm truncate">{subject.name}</h4>
                        <span className="bg-amber-50 text-amber-800 border border-amber-200 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                          Lab
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 font-mono font-medium">{subject.subjectCode}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {assignedFaculty ? (
                        <div className="space-y-1">
                          <p className="text-xs font-bold text-slate-800 truncate max-w-[140px]">{assignedFaculty.facultyName}</p>
                          <span className="text-[9px] text-slate-500 font-bold uppercase tracking-wider bg-slate-200 px-1.5 py-0.5 rounded inline-block">{assignedFaculty.role}</span>
                        </div>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50/50 px-2.5 py-0.5 rounded border border-amber-200/80">Needs Setup</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // -------- EARLY RETURNS --------
  if (loading && !classDetails) {
    // ✅ Check if skipLayout is true
    if (skipLayout) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-xs p-6 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm">
            <FiLoader className="animate-spin text-indigo-600 mx-auto" size={32} />
            <div className="space-y-1">
              <h3 className="text-slate-900 font-bold text-sm uppercase tracking-wider">Loading Data</h3>
              <p className="text-slate-500 text-xs font-medium">Please wait while we open this page...</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <TenantAdminLayout>
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="text-center space-y-4 max-w-xs p-6 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm">
            <FiLoader className="animate-spin text-indigo-600 mx-auto" size={32} />
            <div className="space-y-1">
              <h3 className="text-slate-900 font-bold text-sm uppercase tracking-wider">Loading Data</h3>
              <p className="text-slate-500 text-xs font-medium">Please wait while we open this page...</p>
            </div>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  if (!classDetails) {
    // ✅ Check if skipLayout is true
    if (skipLayout) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="text-center max-w-sm space-y-4 p-6 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm">
            <div className="w-12 h-12 bg-rose-50 text-rose-600 border border-rose-200 rounded-2xl flex items-center justify-center mx-auto shadow-xs">
              <FiAlertCircle size={24} />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-slate-900 font-bold text-base uppercase tracking-wider">Class Not Found</h3>
              <p className="text-slate-600 text-xs font-medium leading-relaxed">We could not find this class in our records.</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <TenantAdminLayout>
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <div className="text-center max-w-sm space-y-4 p-6 bg-slate-50 border border-slate-200 rounded-2xl shadow-sm">
            <div className="w-12 h-12 bg-rose-50 text-rose-600 border border-rose-200 rounded-2xl flex items-center justify-center mx-auto shadow-xs">
              <FiAlertCircle size={24} />
            </div>
            <div className="space-y-1.5">
              <h3 className="text-slate-900 font-bold text-base uppercase tracking-wider">Class Not Found</h3>
              <p className="text-slate-600 text-xs font-medium leading-relaxed">We could not find this class in our records.</p>
            </div>
          </div>
        </div>
      </TenantAdminLayout>
    );
  }

  const programInfo = getProgramInfo();

  // -------- STUDENT SORTING --------
  const getSortedStudents = () => {
    if (!Array.isArray(students)) return [];
    return [...students].sort((a, b) => {
      const usnA = String(a.registrationNumber || a.rollNumber || '').trim().toUpperCase();
      const usnB = String(b.registrationNumber || b.rollNumber || '').trim().toUpperCase();
      return usnA.localeCompare(usnB, undefined, { numeric: true, sensitivity: 'base' });
    });
  };

  const sortedStudentRoster = getSortedStudents();

  // -------- MAIN CONTENT --------
  const content = (
    <>
      <ToastContainer theme="light" />
      
      <div className="w-full min-h-screen bg-slate-100 pb-12">
        {/* Header Block */}
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-6 lg:px-8 shadow-xs">
          <div className="max-w-7xl mx-auto space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex gap-4 items-center">
                <div className="w-12 h-12 bg-indigo-600 text-white rounded-xl flex items-center justify-center shadow-md shadow-indigo-600/10 flex-shrink-0">
                  <FiBook size={22} />
                </div>
                <div className="space-y-1 min-w-0">
                  <h1 className="text-xl lg:text-2xl font-black text-slate-900 tracking-tight flex flex-wrap items-center gap-2">
                    {classDetails.className}
                    <span className="inline-flex items-center gap-1.5 bg-slate-200 text-slate-800 text-xs font-bold px-2.5 py-0.5 rounded-md border border-slate-300">
                      <FiUsers size={12} />
                      {students.length} Students
                    </span>
                  </h1>
                  
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 font-semibold">
                    <span className="flex items-center gap-1 text-slate-800">
                      <FiMapPin className="text-slate-500" size={13} />
                    </span>
                    <span className="text-slate-300">|</span>
                    <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-700 font-mono text-[11px]">{programInfo.typeCode}</span>
                    <span className="text-slate-300">|</span>
                    <span className="flex items-center gap-1 text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                      <FiLayers size={13} />
                      {classDetails.isSemesterBased
                        ? `Semester ${classDetails.semesterNumber}`
                        : classDetails.isTermBased && classDetails.termName
                          ? `Term: ${classDetails.termName}`
                          : 'Full Year'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Top Buttons */}
              <div className="flex items-center gap-2 self-end sm:self-auto flex-wrap">
                <button
                  onClick={() => {
                    fetchClassDetails();
                    fetchFacultyAssignments();
                    fetchStudents();
                    fetchClassSubjects(classId);
                  }}
                  disabled={facultyLoading || subjectsLoading}
                  className="p-2 text-slate-700 border border-slate-300 bg-white rounded-xl hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-40"
                  title="Refresh Page"
                >
                  <FiRefreshCw className={facultyLoading || subjectsLoading ? 'animate-spin text-indigo-600' : ''} size={15} />
                </button>

                {!isCompletedAcademicYear ? (
                  <>
                    <button
                      onClick={handleEditClass}
                      className="px-3.5 py-2 bg-white text-slate-800 border border-slate-300 rounded-xl hover:bg-slate-50 font-bold text-xs tracking-tight shadow-xs transition-all"
                    >
                      Settings
                    </button>
                    <button
                      onClick={handleFacultyAssignment}
                      className="px-3.5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-bold text-xs tracking-tight shadow-sm shadow-indigo-600/10 transition-all"
                    >
                      Assign Teachers
                    </button>
                    <button
                      onClick={() => setShowStudentsModal(true)}
                      className="px-3.5 py-2 bg-slate-900 text-white rounded-xl hover:bg-slate-800 font-bold text-xs tracking-tight shadow-sm transition-all"
                    >
                      Student List
                    </button>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-xl font-bold text-xs">
                    <FiLock size={13} />
                    Year Closed (Locked)
                  </span>
                )}
              </div>
            </div>

            {/* Simple Menu Tabs */}
            <div className="pt-2 border-t border-slate-200 flex gap-6 overflow-x-auto no-scrollbar">
              {[
                { id: 'overview', label: 'Overview Dashboard', icon: FiBarChart2 },
                { id: 'faculty', label: 'Teacher List', icon: FiUsers },
                { id: 'subjects', label: 'Subject List', icon: FiBook }
              ].map((tab) => {
                const TabIcon = tab.icon;
                const isSelected = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 pb-2 px-0.5 text-xs font-bold border-b-2 transition-all whitespace-nowrap ${
                      isSelected
                        ? 'border-indigo-600 text-indigo-600 font-extrabold'
                        : 'border-transparent text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    <TabIcon size={14} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Workspace Content */}
        <div className="max-w-7xl mx-auto px-4 lg:px-8 mt-6">
          {isCompletedAcademicYear && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 text-amber-950 rounded-xl text-xs flex gap-3 shadow-xs">
              <FiLock className="text-amber-700 mt-0.5 flex-shrink-0" size={16} />
              <div>
                <span className="font-extrabold block text-amber-900 text-sm">Locked Record</span>
                This academic year is finished. You cannot edit any data for past years.
              </div>
            </div>
          )}

          {activeTab === 'overview' && renderOverviewTab()}
          {activeTab === 'faculty' && renderFacultyTab()}
          {activeTab === 'subjects' && renderSubjectsTab()}
        </div>
      </div>

      {/* Student Enrollment Table Modal */}
      {showStudentsModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fadeIn">
          <div className="bg-slate-100 rounded-2xl shadow-xl max-w-2xl w-full flex flex-col max-h-[80vh] overflow-hidden border border-slate-200 animate-slideUp">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Student List</h2>
                <p className="text-[11px] text-slate-600 font-medium mt-0.5">{classDetails.className} &bull; Sorted by USN / Roll Number</p>
              </div>
              <button
                onClick={() => setShowStudentsModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-300 bg-white hover:bg-slate-50 transition-colors text-slate-500 hover:text-slate-800"
              >
                <FiX size={15} />
              </button>
            </div>
            
            <div className="overflow-auto flex-1 bg-white">
              {sortedStudentRoster.length === 0 ? (
                <div className="text-center py-12 bg-slate-50/50">
                  <p className="text-xs font-semibold text-slate-500">No active students are found in this class.</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4 text-center w-16">Sl.No</th>
                      <th className="py-3 px-4">USN / Roll No</th>
                      <th className="py-3 px-4">Student Name</th>
                      <th className="py-3 px-4">Email</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 text-xs font-medium text-slate-700">
                    {sortedStudentRoster.map((student, index) => (
                      <tr key={student._id || student.id} className="hover:bg-slate-50/70 transition-colors">
                        <td className="py-3 px-4 text-center text-slate-500 font-mono font-bold bg-slate-50/30">{index + 1}</td>
                        <td className="py-3 px-4">
                          <span className="font-mono font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 border border-indigo-100 rounded">
                            {student.registrationNumber || student.rollNumber || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-900">{student.name}</td>
                        <td className="py-3 px-4 text-slate-500 truncate max-w-[200px]">{student.email || 'No email setup'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            <div className="p-3 bg-slate-50 border-t border-slate-200 text-right">
              <span className="text-[11px] font-bold text-slate-600 bg-slate-200 px-2.5 py-1 border border-slate-300 rounded-lg">
                Total Students: {sortedStudentRoster.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Sub-Modals */}
      {activeModal?.type === 'facultyAssignment' && (
        <FacultyAssignmentModal
          isOpen={true}
          onClose={closeModal}
          classId={activeModal.props.classId}
          className={activeModal.props.className}
          subjects={activeModal.props.subjects || []}
          notify={toast}
          isViewOnly={activeModal.props.isViewOnly || isCompletedAcademicYear}
        />
      )}

      {activeModal?.type === 'labBatchManagement' && (
        <LabBatchManagementModal
          isOpen={true}
          onClose={closeModal}
          classId={activeModal.props.classId}
          className={activeModal.props.className}
          labSubjects={activeModal.props.labSubjects || []}
          notify={toast}
          isViewOnly={activeModal.props.isViewOnly || isCompletedAcademicYear}
          tenantId={tenantId}
          userId={userId}
          organizationId={organizationId}
          isSemesterBased={activeModal.props.isSemesterBased}
          semesterId={activeModal.props.semesterId}
          termId={activeModal.props.termId}
          semesterNumber={activeModal.props.semesterNumber}
          termNumber={activeModal.props.termNumber}
        />
      )}

      {activeModal?.type === 'manageElectiveStudents' && (
        <ElectiveStudentsModal
          isOpen={true}
          onClose={closeModal}
          classId={activeModal.props.classId}
          classObj={activeModal.props.classObj}
          className={activeModal.props.className}
          groupType={activeModal.props.groupType}
          electiveSubjects={activeModal.props.electiveSubjects || []}
          electiveSubjectIds={activeModal.props.electiveSubjectIds || []}
          notify={toast}
          isViewOnly={activeModal.props.isViewOnly || isCompletedAcademicYear}
        />
      )}

      {activeModal?.type === 'editClass' && (
        <EditClassModal
          isOpen={true}
          onClose={closeModal}
          classId={activeModal.props.classId}
          className={activeModal.props.className}
          tenantId={tenantId}
          academicYears={[]}
          semesters={activeModal.props.isSemesterBased ? semesters : []}
          terms={!activeModal.props.isSemesterBased ? terms : []}
          departments={departments}
          programs={allPrograms}
          programTypes={allProgramTypes}
          notify={toast}
          onClassUpdated={() => {
            closeModal();
            toast.success('Class options updated.');
          }}
          isViewOnly={activeModal.props.isViewOnly || isCompletedAcademicYear}
          mappingData={navigationState.academicContext?.programMapping}
        />
      )}
    </>
  );

  // ✅ Final return - conditionally wrap with TenantAdminLayout
  if (skipLayout) {
    return content;
  }

  return (
    <TenantAdminLayout>
      {content}
    </TenantAdminLayout>
  );
};

export default ClassDetailsPage;